/**
 * 風解析。開発メモの「4D Wind Engine」の入口。
 *
 * **気球の対地速度・進行方向は、そのままその高度の風**という前提に立つ。
 * これは原典 GoalView の `norden_bombsight()`（20m×50スロットに自機の
 * 速度・針路を積む / docs/01 §6）と同じモデルで、競技で実績がある。
 * 違うのは、位置（緯度・経度）と UTC 絶対時刻を保持して他機へ一般化できる
 * ようにした点（原典は自機のみ・位置軸なし・会場全体で1本のプロファイル）。
 */

import { deriveMotion } from './igc.ts';
import type { FixSample, WindSample } from './types.ts';

/** 方位が意味を持たなくなる低速の閾値 [m/s]。deriveMotion の判定と揃える */
const DEFAULT_MIN_SPEED = 0.3;

export interface WindOptions {
  /** これ未満の対地速度は方位が定まらないので捨てる [m/s]。既定 0.3 */
  minSpeed?: number;
}

/**
 * 航跡を風サンプル列に変換する。1点 = その高度・その時刻の風。
 *
 * ★ 速度・方位は**報告値をそのまま信じず、位置から算出し直す**。
 *   ライブ端末は `deriveMotion` を通っておらず、`speed`/`heading` は NMEA の
 *   $GPRMC / $VTG 由来。実際の受信機では RMC が 3300 点中 16 点しか出ないもの
 *   があり（位置は GNS/GGA で来る）、多くの点で速度・方位が欠ける。
 *   ここで引き直すことで、IGC 由来かライブ由来かに関わらず同じ土俵に乗る。
 *
 * ★ 入力配列は**変更しない**。`deriveMotion` は破壊的なので、必ず浅いコピーに
 *   対してかける（呼び出し元の `state.flights` などを書き換えてはいけない）。
 */
export function fixesToWindSamples(fixes: FixSample[], options: WindOptions = {}): WindSample[] {
  const minSpeed = options.minSpeed ?? DEFAULT_MIN_SPEED;

  // 浅いコピーを作ってから速度・方位を引き直す（入力を壊さないため）
  const work = fixes.map((f) => ({ ...f }));
  deriveMotion(work);

  const out: WindSample[] = [];
  for (const f of work) {
    // 高度は他の表示と同じ優先順位。競技で採点されるのは気圧高度
    const alt = f.altPressure ?? f.altMsl;
    if (alt == null) continue;
    if (f.speed == null || f.heading == null) continue;
    if (f.speed < minSpeed) continue;

    out.push({
      lat: f.lat,
      lon: f.lon,
      alt,
      t: f.t,
      // heading は「進行方向」＝「気球が流される方向」なので、
      // WindSample.dir の規約（吹いてくる方向ではない）とそのまま一致する
      dir: f.heading,
      speed: f.speed,
      source: f.source,
      terminalId: f.terminalId,
      quality: qualityOf(f),
    });
  }
  return out;
}

/**
 * 観測の確からしさ（0〜1）。
 *
 * いまは GPS の水平精度だけを見る素朴な実装。将来 `source` ごとの重み
 * （公式風・パイバル・ラジオゾンデ）を足すための枠として置いてある。
 */
function qualityOf(f: FixSample): number {
  if (f.accuracy == null) return 1;
  // 精度 5m 以下は満点、50m で 0 に落ちる線形
  const q = 1 - (f.accuracy - 5) / 45;
  return Math.max(0, Math.min(1, q));
}

export interface GridOptions {
  /** 高度ビンの幅 [m]。原典は 20m 固定だった */
  altBandM: number;
  /** 時間ビンの幅 [ms] */
  timeBucketMs: number;
}

/**
 * (時間 × 高度) の格子ごとに風ベクトルを平均して間引く。
 *
 * 1Hz の観測をそのまま矢印にすると重なって潰れるため、表示前に必ず通す。
 *
 * ★★ 平均は必ず**ベクトル（u/v 成分）に分解してから**取ること。
 *     角度をそのまま平均すると 350° と 10° の平均が 180°（真逆）になる。
 *     風の計算で最も踏みやすい典型的なバグなので、テストで固定してある。
 */
export function gridWindSamples(samples: WindSample[], opts: GridOptions): WindSample[] {
  const cells = new Map<
    string,
    { u: number; v: number; lat: number; lon: number; alt: number; t: number; q: number; n: number; sample: WindSample }
  >();

  for (const s of samples) {
    const altBin = Math.round(s.alt / opts.altBandM);
    const timeBin = Math.round(s.t / opts.timeBucketMs);
    const key = `${s.terminalId ?? ''}\0${altBin}\0${timeBin}`;

    const rad = (s.dir * Math.PI) / 180;
    const u = s.speed * Math.sin(rad);
    const v = s.speed * Math.cos(rad);

    const cur = cells.get(key);
    if (cur) {
      cur.u += u;
      cur.v += v;
      cur.lat += s.lat;
      cur.lon += s.lon;
      cur.alt += s.alt;
      cur.t += s.t;
      cur.q += s.quality;
      cur.n += 1;
    } else {
      cells.set(key, {
        u,
        v,
        lat: s.lat,
        lon: s.lon,
        alt: s.alt,
        t: s.t,
        q: s.quality,
        n: 1,
        sample: s,
      });
    }
  }

  const out: WindSample[] = [];
  for (const c of cells.values()) {
    const u = c.u / c.n;
    const v = c.v / c.n;
    out.push({
      lat: c.lat / c.n,
      lon: c.lon / c.n,
      alt: c.alt / c.n,
      t: c.t / c.n,
      dir: ((Math.atan2(u, v) * 180) / Math.PI + 360) % 360,
      speed: Math.hypot(u, v),
      source: c.sample.source,
      terminalId: c.sample.terminalId,
      quality: c.q / c.n,
    });
  }
  // 時系列順に並べておく（描画側が前提にできるように）
  out.sort((a, b) => a.t - b.t);
  return out;
}
